import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ScrollToTop — mount ONCE inside <BrowserRouter>.
 *
 * Behaviour:
 *  - PUSH / REPLACE navigation (Link click) with no hash → jump to top (0,0).
 *  - PUSH / REPLACE with #anchor → smooth-scroll to the anchor element.
 *  - POP (browser back / forward) → do nothing; let the browser restore its
 *    natural scroll position.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    // Preserve natural back/forward behaviour
    if (navType === 'POP') return;

    // In-page anchor — scroll to element rather than top
    if (hash) {
      const id = hash.slice(1);
      // Defer to next tick so the target element is mounted after route change
      window.requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo(0, 0);
        }
      });
      return;
    }

    // Default — jump to top instantly
    window.scrollTo(0, 0);
  }, [pathname, hash, navType]);

  return null;
};

export default ScrollToTop;
